/**
 * @file 建房 / 入座（React 外壳）—— 走 M3 的 HTTP API。
 */

import { useState } from 'react';
import type { CreateRoomResponse, JoinRoomResponse } from '@majiang/protocol';
import { API_ROUTES } from '@majiang/protocol';
import type { OnlineConnInfo } from '../session';

export interface RoomScreenProps {
  readonly onBack: () => void;
  readonly onEnterGame: (conn: OnlineConnInfo) => void;
}

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = (await res.json()) as T & { readonly error?: string; readonly message?: string };
  if (!res.ok || data.error !== undefined) {
    throw new Error(data.message ?? data.error ?? `HTTP ${res.status}`);
  }
  return data;
}

function defaultNick(): string {
  return `麻友${Math.floor(1000 + Math.random() * 9000)}`;
}

export function RoomScreen({ onBack, onEnterGame }: RoomScreenProps): React.JSX.Element {
  const [nick, setNick] = useState<string>(() => defaultNick());
  const [joinCode, setJoinCode] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const res = await postJson<CreateRoomResponse>(API_ROUTES.createRoom, {
        nickname: nick.trim() || defaultNick(),
        rulePatch: {},
      });
      onEnterGame({ roomCode: res.roomCode, seatToken: res.seatToken, wsPath: res.wsPath, nickname: nick.trim() || defaultNick() });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const join = async (): Promise<void> => {
    const code = joinCode.trim().toUpperCase();
    if (code.length === 0) {
      setError('请输入房间码');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await postJson<JoinRoomResponse>(API_ROUTES.joinRoom(code), {
        nickname: nick.trim() || defaultNick(),
        seatToken: null,
      });
      onEnterGame({ roomCode: res.roomCode, seatToken: res.seatToken, wsPath: res.wsPath, nickname: nick.trim() || defaultNick() });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="shell">
      <div className="card">
        <h1>联机好友房</h1>
        <p className="subtitle">本地开发时经 Vite 代理转发到 :8787（wrangler dev）</p>
        <div className="divider" />

        <div>
          <label className="label">昵称</label>
          <input className="input" value={nick} maxLength={24} onChange={(e) => setNick(e.target.value)} />
        </div>

        <div className="row">
          <button className="btn" disabled={busy} onClick={() => void create()}>
            创建房间
          </button>
        </div>

        <div className="divider" />

        <div>
          <label className="label">房间码</label>
          <input
            className="input"
            value={joinCode}
            maxLength={6}
            placeholder="如 AB3K9X"
            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
          />
        </div>
        <div className="row">
          <button className="btn ghost" disabled={busy} onClick={() => void join()}>
            加入房间
          </button>
        </div>

        {error !== null && <p className="error">{error}</p>}

        <div className="divider" />
        <button className="btn ghost" onClick={onBack}>
          返回主菜单
        </button>
      </div>
    </main>
  );
}
